import { CARD_BACKS, useCardBack, type CardBackOption } from '../cardBacks.js';
import { useI18n } from '../i18n.js';
import { useGameState } from '../net/GameProvider.js';
import { CardUI } from './CardUI.js';
import { trackEvent } from '../analytics.js';
import { useSafeBackdropDismiss } from '../utils/modalUtils.js';

interface CardBackModalProps {
  onClose: () => void;
  onOpenProfile?: () => void;
}

export function CardBackModal({ onClose, onOpenProfile }: CardBackModalProps) {
  const { cardBack, setCardBack } = useCardBack();
  const { state } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const isPaidTier = state.profile?.tier === 'paid';
  const backdropHandlers = useSafeBackdropDismiss(onClose);

  const handleSelectOption = (opt: CardBackOption) => {
    if (opt.paidOnly && !isPaidTier) {
      trackEvent('card_back_locked_clicked', { card_back_id: opt.id });
      if (onOpenProfile) {
        onClose();
        onOpenProfile();
      }
      return;
    }
    trackEvent('card_back_selected', { card_back_id: opt.id });
    setCardBack(opt.id);
  };

  return (
    <div
      className="modal-overlay"
      {...backdropHandlers}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(4, 22, 16, 0.94)',
        zIndex: 200,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain',
        padding: '24px 16px 48px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 780,
          background: '#0d4735',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 16,
          boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
          padding: '28px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.6em' }}>🎴 {t.cardBackTitle}</h2>
            <p style={{ margin: '6px 0 0', opacity: 0.8, fontSize: '0.92em' }}>{t.cardBackSubtitle}</p>
          </div>
          <button onClick={onClose} style={{ padding: '8px 18px' }}>
            ✕ {t.closeRules}
          </button>
        </div>

        {/* Paid Tier Gating Banner */}
        {!isPaidTier && (
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(217, 119, 6, 0.18))',
              border: '1px solid rgba(251, 191, 36, 0.55)',
              borderRadius: 12,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: '0.88em', color: '#fde68a', lineHeight: 1.45 }}>
              <strong>👑 {isMn ? 'Төлбөртэй (VIP) эрхийн давуу тал:' : 'Paid Tier (VIP) Exclusive:'}</strong>{' '}
              {isMn
                ? 'Зөвхөн Төлбөртэй эрхтэй хэрэглэгчид тусгай хөзрийн ар загвар болон тусгай эмоци ашиглах, мөн долоо хоног бүр +160 🍵 Цай авах эрхтэй.'
                : 'Only Paid Tier members can equip custom card backs, send premium emotes, and claim +160 🍵 Tsai every week.'}
            </div>
            {state.profile ? (
              <button
                type="button"
                onClick={onOpenProfile}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                  color: '#000',
                  fontWeight: 800,
                  padding: '8px 14px',
                  fontSize: '0.86em',
                  whiteSpace: 'nowrap',
                }}
              >
                👑 {isMn ? 'Төлбөртэй эрх идэвхжүүлэх' : 'Upgrade to Paid Tier'}
              </button>
            ) : (
              onOpenProfile && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenProfile();
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                    color: '#000',
                    fontWeight: 800,
                    padding: '8px 14px',
                    fontSize: '0.86em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  👤 {isMn ? 'Нэвтэрч VIP эрх авах' : 'Sign In for Paid Tier'}
                </button>
              )
            )}
          </div>
        )}

        {/* Grid of Card Back Options */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {CARD_BACKS.map((opt) => {
            const isLocked = !!opt.paidOnly && !isPaidTier;
            const isSelected = cardBack === opt.id;
            const badge = isMn ? opt.badgeMn : opt.badgeEn;
            return (
              <div
                key={opt.id}
                onClick={() => handleSelectOption(opt)}
                style={{
                  background: isSelected ? 'rgba(74, 222, 128, 0.14)' : 'rgba(0, 0, 0, 0.4)',
                  border: isSelected
                    ? '2px solid var(--accent-color)'
                    : isLocked
                    ? '2px solid rgba(251, 191, 36, 0.25)'
                    : '2px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: 12,
                  padding: '18px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 12,
                  cursor: 'pointer',
                  position: 'relative',
                  opacity: isLocked ? 0.85 : 1,
                  transition: 'transform 0.15s, border-color 0.15s',
                }}
              >
                {badge && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      background: opt.paidOnly ? 'rgba(251, 191, 36, 0.22)' : 'rgba(74, 222, 128, 0.2)',
                      color: opt.paidOnly ? '#fde68a' : '#4ade80',
                      border: opt.paidOnly
                        ? '1px solid rgba(251, 191, 36, 0.5)'
                        : '1px solid rgba(74, 222, 128, 0.45)',
                      fontSize: '0.7em',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 999,
                    }}
                  >
                    {isLocked ? `🔒 ${badge}` : badge}
                  </span>
                )}

                {/* Fanned 2-card preview */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px 0' }}>
                  <CardUI
                    faceDown
                    cardBackId={opt.id}
                    style={{ transform: 'rotate(-6deg)', boxShadow: '0 6px 14px rgba(0,0,0,0.45)' }}
                  />
                  <CardUI
                    faceDown
                    cardBackId={opt.id}
                    style={{ marginLeft: -42, transform: 'rotate(6deg)', boxShadow: '0 6px 14px rgba(0,0,0,0.5)' }}
                  />
                </div>

                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.05em', color: isSelected ? 'var(--accent-color)' : '#fff' }}>
                    {isMn ? opt.nameMn : opt.nameEn}
                  </div>
                  <div style={{ fontSize: '0.82em', opacity: 0.78, marginTop: 4, lineHeight: 1.4 }}>
                    {isMn ? opt.descMn : opt.descEn}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectOption(opt);
                  }}
                  style={{
                    width: '100%',
                    marginTop: 'auto',
                    background: isSelected
                      ? 'var(--accent-color)'
                      : isLocked
                      ? 'rgba(251, 191, 36, 0.18)'
                      : 'rgba(255,255,255,0.14)',
                    color: isSelected ? '#000' : isLocked ? '#fde68a' : '#fff',
                    border: isLocked ? '1px solid rgba(251, 191, 36, 0.45)' : undefined,
                    padding: '7px 12px',
                    fontSize: '0.88em',
                  }}
                >
                  {isSelected
                    ? t.cardBackSelected
                    : isLocked
                    ? isMn
                      ? '🔒 Зөвхөн Төлбөртэй эрх'
                      : '🔒 Paid Tier Only'
                    : t.cardBackChoose}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 28px', fontSize: '1em' }}>
            {t.closeRules}
          </button>
        </div>
      </div>
    </div>
  );
}
